import {
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  BaseGetByFileIdRequest,
  BaseGetByIdRequest,
  BaseGetSearchCountByIdRequest,
} from '@app-types/base/base';
import { ActivatedRoute } from '@angular/router';
import { MobileObserverService } from '@app-services/adaptive/mobile-observer.service';
import { TemplateClient } from '@app-services/api/clients/template.client';
import { TemplateContract } from '@app-types/api/template';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { MatchError } from 'src/app/services/errors/error-matcher';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { SearchBarComponent } from '@app-components/common/search-bar/search-bar.component';
import { NoDataComponent } from '@app-components/common/no-data/no-data.component';
import { SomethingWentWrongComponent } from '@app-components/common/error/something-went-wrong/something-went-wrong.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { DrawerService } from '@app-services/drawer.service';
import {
  MAIL_ACCOUNT_ID,
  SETTING_SETTINGS,
  defaultFilter,
} from '@app-shared/constants/constants';
import { MailAccountTemplateCreateDialogComponent } from '@app-components/settings/organization/mail-account/mail-account-template-create-dialog/mail-account-template-create-dialog.component';
import { DatePipe } from '@angular/common';
import { DeleteDialogComponent } from '@app-components/common/delete-dialog/delete-dialog.component';
import { Subscription } from 'rxjs';
import { SkeletonTableComponent } from '@app-components/common/skeletons/skeleton-table/skeleton-table.component';
import { InfinityScrollComponent } from '@app-components/common/infinity-scroll/infinity-scroll.component';
import { TableHeaderComponent } from '@app-components/common/table-header/table-header.component';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { FilterFields, OptionsEnum } from '@app-types/common';
import { TemplateSearchOrder } from '@app-types/enums/TemplateSearchOrder';
import { FormatTemplatesSearchOrders } from '@app-pipes/format-templates-search-orders.pipe';
import { OrderDirection } from '@app-types/enums/order-direction';
import { LoaderComponent } from '@app-components/common/loader/loader.component';
import { TempFileClient } from '@app-services/api/clients/temp-file.client';

const { PAGE_SIZE, STEP_FETCH } = SETTING_SETTINGS;

@Component({
  selector: 'app-mail-account-templates',
  templateUrl: './mail-account-templates.component.html',
  styleUrls: ['./mail-account-templates.component.scss'],
  imports: [
    TranslateModule,
    MatButtonModule,
    MatIconModule,
    SearchBarComponent,
    NoDataComponent,
    SomethingWentWrongComponent,
    MatTableModule,
    MatTooltipModule,
    MatCardModule,
    DatePipe,
    MailAccountTemplateCreateDialogComponent,
    SkeletonTableComponent,
    TableHeaderComponent,
    InfinityScrollComponent,
    MatSort,
    MatSortModule,
    LoaderComponent,
  ],
})
export class MailAccountTemplatesComponent implements OnInit, OnDestroy {
  @HostBinding('class') className = 'setting-container';
  public organizationId: number;
  public mailAccountId: number;

  public isLoading = true;
  public isError = false;

  public searchValue = '';
  public canNavigateForward = false;
  public isLoadingScroll = false;
  public isErrorScroll = false;
  public offset = 0;
  public stepFetch = STEP_FETCH;
  public pageSize = PAGE_SIZE;
  public totalSize = 0;
  public editingTemplateId: number | null;
  isReloadLoading = false;
  private suppressSortDrivenSearch = false;
  public dataSource = new MatTableDataSource<TemplateContract>([]);
  public displayedColumns: string[] = [
    'templateName',
    'templateDesc',
    'modifyDate',
    'actions',
  ];
  private useMobileViewSubscription: Subscription;
  public useMobileView = false;
  public searchOrder: TemplateSearchOrder | 0 = 0;
  public searchOrders: OptionsEnum[];
  public filter: FilterFields<TemplateSearchOrder> = {
    ...defaultFilter,
  };
  private formatTemplatesSearchOrders = new FormatTemplatesSearchOrders();
  public isExporting = false;

  @ViewChild('createDrawer') createDrawer: TemplateRef<any>;
  @ViewChild(InfinityScrollComponent) infinityScroll: InfinityScrollComponent;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    private route: ActivatedRoute,
    private mobileObserverService: MobileObserverService,
    public matchError: MatchError,
    private templateClient: TemplateClient,
    public dialog: MatDialog,
    private drawerService: DrawerService,
    private tempFileClient: TempFileClient
  ) {}

  async ngOnInit(): Promise<void> {
    this.useMobileViewSubscription = this.mobileObserverService
      .mobileObserver()
      .subscribe(isMobile => (this.useMobileView = isMobile));

    this.mailAccountId = parseInt(
      this.route.snapshot.paramMap.get(MAIL_ACCOUNT_ID) as string,
      10
    );

    this.searchOrders = this.displayedColumns
      .filter(el => el !== 'actions')
      .map(key => ({
        id: this.formatTemplatesSearchOrders.transform(key),
        name: key,
      }));

    await this.loadTemplates(true);
  }

  public async loadTemplates(fetchCounter: boolean): Promise<void> {
    this.canNavigateForward = false;
    const loaderTimeout =
      !this.offset && !this.dataSource.data.length
        ? setTimeout(() => {
            this.isLoading = true;
          }, 500)
        : undefined;

    this.offset && (this.isLoadingScroll = true);
    this.isError = false;
    this.isErrorScroll = false;

    try {
      const nullableSearchValue = this.searchValue ? this.searchValue : null;

      const request = {
        mailAccountId: this.mailAccountId,
        offset: this.offset,
        fetch: this.stepFetch,
        filter: this.filter,
        search: nullableSearchValue,
      };
      const response = await this.templateClient.searchForMailAccount(request);
      this.dataSource.data =
        this.offset === 0
          ? response.data
          : [...this.dataSource.data, ...response.data];

      if (fetchCounter) {
        const countRequest = new BaseGetSearchCountByIdRequest(
          this.mailAccountId,
          nullableSearchValue
        );
        const counterResponse =
          await this.templateClient.getSearchCountForMailAccount(countRequest);
        this.totalSize = counterResponse.result;
      }
      this.canNavigateForward = this.totalSize !== this.dataSource.data.length;
    } catch (e) {
      if (this.isLoadingScroll) {
        this.isErrorScroll = true;
      } else {
        this.isError = true;
      }
      this.matchError.logError(e);
    } finally {
      loaderTimeout && clearTimeout(loaderTimeout);
      this.isLoading && (this.isLoading = false);
      this.isLoadingScroll && (this.isLoadingScroll = false);
    }
  }

  public onSearchValueChanged(newSearchValue: string): void {
    this.searchValue = newSearchValue;
  }

  public openCreateModelForm(): void {
    this.drawerService.openDrawer(this.createDrawer, null, 'create-email');
  }

  public openEditModelForm(template: TemplateContract): void {
    this.editingTemplateId = template.templateId;
    this.drawerService.openDrawer(this.createDrawer, null, 'create-email');
  }

  public onCloseCreateModalForm(): void {
    this.editingTemplateId && (this.editingTemplateId = null);
  }

  public async onCreateTemplate(): Promise<void> {
    await this.onSearch();
  }

  async deleteTemplate(templateId: number): Promise<void> {
    await this.templateClient.delete(new BaseGetByIdRequest(templateId));
  }

  public openConfirmDialog(templateId: number, name: string): void {
    const dialogRef = this.dialog.open(DeleteDialogComponent, {
      width: '450px',
      autoFocus: false,
      data: {
        title: 'templateDeleteTitle',
        subTitles: [
          {
            title: 'templateDelete',
            subTitle: name,
          },
        ],
        onDelete: async () => await this.deleteTemplate(templateId),
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.isDeleted) {
        this.onSearch();
      }
    });
  }

  public countTo(): string {
    return this.dataSource.data.length.toString();
  }

  public async onScroll(): Promise<void> {
    this.offset = this.offset + this.pageSize;
    await this.loadTemplates(false);
  }

  async refreshData(): Promise<void> {
    await this.loadTemplates(false);
  }

  resetScrollData(): void {
    this.offset = 0;
  }

  async reload(): Promise<void> {
    this.isReloadLoading = true;
    this.filter = { ...defaultFilter };
    this.searchOrder = 0;
    this.suppressSortDrivenSearch = true;
    this.clearSort();
    try {
      await this.onSearch();
    } finally {
      this.isReloadLoading = false;
      setTimeout(() => {
        this.suppressSortDrivenSearch = false;
      }, 0);
    }
  }

  async onSearch(fetchSearchCounter = true): Promise<void> {
    this.resetScrollData();
    this.infinityScroll?.scrollTop();
    await this.loadTemplates(fetchSearchCounter);
  }

  clearSort(): void {
    if (!this.sort) {
      return;
    }
    this.sort.sort({ id: '', start: 'asc', disableClear: false });
    this.sort.active = '';
    this.sort.direction = '';
  }

  async changeOrder(): Promise<void> {
    this.filter.orderDirection =
      this.filter.orderDirection === OrderDirection.Ascending
        ? OrderDirection.Descending
        : OrderDirection.Ascending;
    await this.onSearch();
  }

  public async searchOrderChange($event: any): Promise<void> {
    this.searchOrder = $event as TemplateSearchOrder;
    this.filter.searchOrder = this.searchOrder;
    this.filter.orderDirection = OrderDirection.Ascending;
    await this.onSearch();
  }

  async sortData(sort: Sort): Promise<void> {
    if (this.suppressSortDrivenSearch) {
      return;
    }
    this.filter.searchOrder = sort.direction
      ? this.formatTemplatesSearchOrders.transform(sort.active)
      : TemplateSearchOrder.OrderByTemplateId;

    this.filter.orderDirection =
      sort.direction === 'desc'
        ? OrderDirection.Descending
        : OrderDirection.Ascending;

    await this.onSearch();
  }

  async exportExcel(): Promise<void> {
    this.isExporting = true;
    try {
      const fileResponse = await this.templateClient.exportExcel(
        new BaseGetByIdRequest(this.mailAccountId)
      );
      const downloadRequest = new BaseGetByFileIdRequest(
        fileResponse.result.fileId
      );
      await this.tempFileClient.download(downloadRequest);
    } catch (e) {
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    } finally {
      this.isExporting = false;
    }
  }

  async retryData(): Promise<void> {
    await this.loadTemplates(true);
    setTimeout(() => {
      this.infinityScroll?.reconnect();
    }, 1000);
  }

  ngOnDestroy(): void {
    this.useMobileViewSubscription?.unsubscribe();
  }
}
